'use client';

import { SimpleFundingRateRecord } from 'src/@types/types';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { useEffect, useState } from 'react';
import { getPerpMarketSymbol } from 'src/utils/DataApiClient';
import StatsApiClient from 'src/utils/StatsApiClient';

/**
 * Convert Data API funding rate record to SimpleFundingRateRecord.
 * Applies same transformations as UI_UTILS.getSimpleFundingRateRecord:
 * - Timestamp converted to milliseconds
 * - Funding rates normalized by oracle price and converted to percentage
 * - isAsymmetric flag based on long/short rate comparison
 */
const convertDataApiFundingRate = (record: {
	ts: number;
	marketIndex: number;
	fundingRate: string;
	fundingRateLong: string;
	fundingRateShort: string;
	oraclePriceTwap: string;
	markPriceTwap: string;
}): SimpleFundingRateRecord => {
	const oraclePriceTwap = parseFloat(record.oraclePriceTwap);
	const markPriceTwap = parseFloat(record.markPriceTwap);

	// Data API returns funding rates as raw decimal values that need to be
	// divided by oracle price and multiplied by 100 to get percentage
	// This matches the calculation in UI_UTILS.getFundingRate
	const calculateFundingRatePct = (rate: string): number => {
		const rateNum = parseFloat(rate);
		if (oraclePriceTwap === 0) return 0;
		return (rateNum / oraclePriceTwap) * 100;
	};

	const fundingRate = calculateFundingRatePct(record.fundingRate);
	const fundingRateLong = calculateFundingRatePct(record.fundingRateLong);
	const fundingRateShort = calculateFundingRatePct(record.fundingRateShort);
	const isAsymmetric = fundingRateLong !== fundingRateShort;

	return {
		marketIndex: record.marketIndex,
		ts: record.ts * 1000, // Convert seconds to milliseconds
		fundingRate,
		fundingRateLong,
		fundingRateShort,
		marketPrice: markPriceTwap,
		isAsymmetric,
	};
};

const useMarketFundingHistory = (
	marketIndex: number
): { data: SimpleFundingRateRecord[]; loading: boolean } => {
	const setStore = useDriftStore((s) => s.set);
	const storedFundingHistory = useDriftStore(
		(s) => s.marketFundingHistory[marketIndex]
	);
	const [fundingHistory, setFundingHistory] = useState<
		SimpleFundingRateRecord[]
	>([]);
	const [loading, setLoading] = useState(false);

	const getAndSetFundingRates = async (marketIndex: number) => {
		setLoading(true);

		// Convert marketIndex to symbol for the Data API
		const symbol = getPerpMarketSymbol(marketIndex);

		if (!symbol) {
			console.error('Could not find perp market for index:', marketIndex);
			setLoading(false);
			return;
		}

		// Fetch up to 750 records (full month of hourly funding rates)
		const fundingRatesResult = await StatsApiClient.fetchFundingRates(symbol);

		if (fundingRatesResult?.success && fundingRatesResult.records) {
			const fundingRates = fundingRatesResult.records
				.map(convertDataApiFundingRate)
				.sort(
					(a: SimpleFundingRateRecord, b: SimpleFundingRateRecord) =>
						b.ts - a.ts
				);

			setStore((s) => {
				s.marketFundingHistory = {
					[marketIndex]: fundingRates,
					...s.marketFundingHistory,
				};
			});

			setFundingHistory(fundingRates);
		} else {
			console.error('fundingRates load failed');
		}
		setLoading(false);
	};

	useEffect(() => {
		if (marketIndex === undefined) {
			setFundingHistory([]);
			setLoading(false);
			return;
		}

		if (!storedFundingHistory || storedFundingHistory?.length === 0) {
			// fetch and store funding history if it hasn't already been fetched
			getAndSetFundingRates(marketIndex);
		} else {
			setFundingHistory(storedFundingHistory);
			setLoading(false);
		}
	}, [marketIndex]);

	return { data: fundingHistory, loading };
};

export default useMarketFundingHistory;
